import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../api/client";
import MetricCard from "../../components/MetricCard";
import {
  BREAKDOWN_STATUS,
  PM_STATE,
  formatDate,
  formatDuration,
  formatHours,
  formatPercent,
  machineLabel,
  pmDueText,
} from "../assets/assetUtils";
import "../assets/Assets.css";
import "./InventoryOverviewTab.css";

const panel = { background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" };
const panelHead = { padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 };
const empty = { padding: 32, textAlign: "center", color: "#94a3b8" };
const viewAll = { fontSize: 12, color: "#1d4ed8", fontWeight: 700, textDecoration: "none" };

function Pill({ meta }) {
  return meta ? <span className={`am-pill ${meta.tone}`}>{meta.label}</span> : null;
}

// What is down right now, what maintenance is due, and the last 30 days'
// reliability — the shop-floor view of Asset Management.
export default function AssetOverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/manufacturing/assets/overview");
      setData(res.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't load asset status.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Downtime is live, so this refreshes like the inventory tab does.
  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const m = data?.machines;
  const rel = data?.reliability;
  const open = data?.openBreakdowns || [];
  const pm = [...(data?.pmDue || []), ...(data?.pmDueSoon || [])];
  const warranties = data?.warranties || [];
  const v = (value) => (loading ? "—" : value);

  return (
    <div>
      <div className="dash-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Asset Overview</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>Machines down, maintenance due and reliability over the last 30 days.</p>
        </div>
        <Link to="/manufacturing/assets" className="btn-secondary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
          Open Asset Management
        </Link>
      </div>

      {error && <div className="am-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
        <MetricCard label="Machines" value={v(m ? m.total - m.retired : null)} />
        <MetricCard label="Running" value={v(m?.running)} accent="#16a34a" />
        <MetricCard label="Down Now" value={v(m?.down)} accent={m?.down ? "#dc2626" : undefined} />
        <MetricCard label="PM Due / Overdue" value={v(data?.pmDue?.length)} accent={data?.pmDue?.length ? "#d97706" : undefined} />
      </div>
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <MetricCard label="Availability (30d)" value={v(formatPercent(rel?.availability))} />
        <MetricCard label="Downtime (30d)" value={v(formatHours(rel?.downtimeHours))} accent={rel?.downtimeHours ? "#dc2626" : undefined} />
        <MetricCard label="MTTR (30d)" value={v(formatHours(rel?.mttrHours))} />
        <MetricCard label="MTBF (30d)" value={v(formatHours(rel?.mtbfHours))} />
      </div>

      <div style={{ ...panel, marginBottom: 16, ...(open.length ? { borderColor: "#fca5a5" } : {}) }}>
        <div style={panelHead}>
          <h2 style={{ margin: 0, fontSize: 15, color: open.length ? "#b91c1c" : "#0f2d5c", display: "flex", alignItems: "center", gap: 8 }}>
            {open.length > 0 && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />}
            Machines Down
          </h2>
          <Link to="/manufacturing/assets?tab=breakdowns" style={viewAll}>All breakdowns →</Link>
        </div>
        {loading ? (
          <div style={empty}>Loading…</div>
        ) : open.length === 0 ? (
          <div style={{ ...empty, color: "#15803d", fontWeight: 600 }}>Every machine is running.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="am-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Machine</th>
                  <th>What happened</th>
                  <th>Reported by</th>
                  <th>Status</th>
                  <th className="am-num">Down for</th>
                </tr>
              </thead>
              <tbody>
                {open.map((b) => (
                  <tr key={b.id}>
                    <td><Link to={`/manufacturing/assets/machines/${b.machineId}?breakdown=${b.id}`} className="am-strong">{b.label}</Link></td>
                    <td>{machineLabel(b.machine)}{b.machine?.location && <div className="am-muted">{b.machine.location}</div>}</td>
                    <td><div className="am-clip">{b.description}</div></td>
                    <td>{b.reportedBy?.name || "—"}</td>
                    <td><Pill meta={BREAKDOWN_STATUS[b.status]} /></td>
                    <td className="am-num" style={{ color: "#b91c1c", fontWeight: 700 }}>{formatDuration(b.downtimeMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }} className="inv-overview-grid">
        <div style={panel}>
          <div style={panelHead}>
            <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Preventive Maintenance Due</h2>
            <Link to="/manufacturing/assets?tab=maintenance" style={viewAll}>View all →</Link>
          </div>
          {loading ? (
            <div style={empty}>Loading…</div>
          ) : pm.length === 0 ? (
            <div style={{ ...empty, color: "#15803d", fontWeight: 600 }}>Nothing due in the coming days.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="am-table">
                <tbody>
                  {pm.map((s) => (
                    <tr key={s.id}>
                      <td><span className="am-strong">{s.title}</span><div className="am-muted">{machineLabel(s.machine)}</div></td>
                      <td>{pmDueText(s)}</td>
                      <td><Pill meta={PM_STATE[s.due.state]} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={panel}>
          <div style={panelHead}>
            <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Warranties Ending Soon</h2>
          </div>
          {loading ? (
            <div style={empty}>Loading…</div>
          ) : warranties.length === 0 ? (
            <div style={empty}>No warranty or AMC ends in the next 30 days.</div>
          ) : (
            <div style={{ padding: "6px 0" }}>
              {warranties.map((w) => (
                <div key={w.id} style={{ padding: "10px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <Link to={`/manufacturing/assets/machines/${w.machine.id}`} style={{ fontWeight: 700, color: "#0f2d5c", fontSize: 13, textDecoration: "none" }}>{machineLabel(w.machine)}</Link>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{w.provider} · ends {formatDate(w.endDate)}</div>
                  </div>
                  <span className={`am-pill ${w.state === "expired" ? "bad" : "warn"}`}>{w.state === "expired" ? "Expired" : "Expiring"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
